/**
 * The lifecycle policy screen (contract §9): when the trial/dunning emails fire, how long
 * the trial and grace windows are, and which of the nine moments are on. The email TEXT is
 * not editable here — it stays in `backend/emails/platform.py`'s fr/en/es templates.
 *
 * Save opens `StepUpConfirmDialog` (gateway) exactly like `affiliate/ProgramPolicyForm.jsx`:
 * a money-adjacent write (it changes when a paying practice gets suspended) behind the
 * platform's one step-up primitive. A `POLICY_INVALID` 422 is NOT an identity failure — the
 * step-up token is deliberately spent AFTER validation (contract §6, "Validating first means
 * a rejected patch never burns a step-up token"), so re-offering step-up 2 for a validation
 * problem would blame the operator's identity for a value they typed. `confirmSave` below
 * closes the dialog itself on that outcome and leaves the per-field violations, already
 * filled in by `useLifecyclePolicy`, on the form; only REAUTH_REQUIRED re-shows step 2.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Switch } from "../../components/ui/switch";
import { Button } from "../../components/ui/button";
import LoadFailure from "../../components/ui/LoadFailure";
import StepUpConfirmDialog from "../../gateway/components/StepUpConfirmDialog";
import { useT, useLanguage } from "../../lib/LanguageContext";
import { formatDateTime } from "../../lib/dateTime";
import { LIFECYCLE } from "../../constants/testIds/lifecycle";
import { useScreenWrites } from "../screenWrites";
import { EMAIL_TOGGLES, LIFECYCLE_BOUNDS, LIFECYCLE_ERROR_CODES } from "./contract";
import useLifecyclePolicy from "./useLifecyclePolicy";
import lifecycleApi from "./api";
import LeadDaysEditor from "./LeadDaysEditor";
import UpcomingSends from "./UpcomingSends";
import RecentSends from "./RecentSends";

const getPath = (obj, path) => path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
const TOGGLE_PATH_BY_MOMENT = Object.fromEntries(EMAIL_TOGGLES.map(([path, moment]) => [moment, path]));

function FieldViolations({ path, violations, t }) {
  const rules = violations[path];
  if (!rules || rules.length === 0) return null;
  return (
    <p id={LIFECYCLE.fieldError(path)} data-testid={LIFECYCLE.fieldError(path)} role="alert" className="text-xs text-rose-600 mt-1">
      {rules.map((r) => t(`lifecycle.violation.${r}`)).join(" ")}
    </p>
  );
}

function EmailToggle({ moment, draft, setField, violations, t, disabled }) {
  const path = TOGGLE_PATH_BY_MOMENT[moment];
  const checked = !!getPath(draft, path);
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <label htmlFor={`lifecycle-toggle-${moment}`} className="text-sm text-slate-700">
        {t(`lifecycle.moment.${moment}`)}
      </label>
      <Switch
        id={`lifecycle-toggle-${moment}`}
        checked={checked}
        onCheckedChange={(v) => setField(path, v === true)}
        disabled={disabled}
        data-testid={LIFECYCLE.toggle(path)}
      />
      <FieldViolations path={path} violations={violations} t={t} />
    </div>
  );
}

export default function LifecyclePolicyPanel({ api = lifecycleApi }) {
  const t = useT("superadmin");
  const tv = useT("common");
  const { lang } = useLanguage();
  const {
    status, bounds, stripeControlled, updatedAt, updatedBy,
    draft, dirty, setField, reset, save, violations, saving, reload,
  } = useLifecyclePolicy({ api });

  const [confirming, setConfirming] = useState(false);
  const [formError, setFormError] = useState("");
  const [justSaved, setJustSaved] = useState(false);
  const [sendsRefresh, setSendsRefresh] = useState(0);

  useEffect(() => { if (dirty) setJustSaved(false); }, [dirty]);
  // Saving the policy is settings.write behind a step-up whose mint needs finance.write
  // (screenWrites.js); without both the form is shown read-only, with no Save to offer.
  const { lifecyclePolicy: canSave, lifecycleRun: canRun } = useScreenWrites("automation");
  const readOnly = !canSave;

  if (status === "loading") {
    return <p data-testid={LIFECYCLE.loading} className="text-slate-400 text-sm py-8 text-center">{t("lifecycle.loading")}</p>;
  }
  if (status === "error" || !draft) {
    return (
      <LoadFailure
        message={t("lifecycle.loadFailed")}
        onRetry={reload}
        retryLabel={tv("actions.retry")}
        testId={LIFECYCLE.loadError}
        retryTestId={LIFECYCLE.retry}
      />
    );
  }

  // The server's `bounds` win; the contract mirror is the fallback, never a second literal.
  const b = { ...LIFECYCLE_BOUNDS, ...(bounds || {}) };
  const trialLengthBounds = b.trial_length_days;
  const leadDayBounds = b.reminder_lead_days;
  const graceBounds = b.grace_days;
  const followUpMinDay = b.follow_up_day.min;
  const maxReminders = b.max_reminders;
  const maxFollowUps = b.max_follow_ups;

  const trialLength = Number(draft.trial.length_days);
  const graceDays = Number(draft.dunning.grace_days);

  const confirmSave = async ({ stepUpToken }) => {
    setFormError("");
    const result = await save(stepUpToken);
    if (result.ok) {
      toast.success(t("lifecycle.saved"));
      setJustSaved(true);
      return { ok: true };
    }
    if (result.code === "REAUTH_REQUIRED") {
      // The only outcome worth a second identity check: the token itself raced or expired.
      return { ok: false, code: result.code };
    }
    // A validation failure (per-field violations already filled in by the hook) or any
    // unclassified error is not an identity problem — close the dialog now rather than
    // invite a pointless re-verification.
    setConfirming(false);
    if (result.code !== "POLICY_INVALID") {
      const code = LIFECYCLE_ERROR_CODES.includes(result.code) ? result.code : "UNKNOWN";
      setFormError(t(`lifecycle.error.${code}`));
    }
    return { ok: false, code: null };
  };

  return (
    <div data-testid={LIFECYCLE.panel}>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="font-sans font-semibold text-2xl text-slate-800">{t("lifecycle.title")}</h2>
          <p className="text-slate-500 text-sm mt-1">{t("lifecycle.subtitle")}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-3">
            {dirty && <span data-testid={LIFECYCLE.dirtyBadge} className="text-xs text-amber-600">{t("lifecycle.unsavedChanges")}</span>}
            {justSaved && (
              <span data-testid={LIFECYCLE.savedToast} role="status" className="text-xs text-emerald-600">
                {t("lifecycle.saved")}
              </span>
            )}
          </div>
          {updatedAt && (
            <p data-testid={LIFECYCLE.lastSaved} className="text-xs text-slate-400">
              {t("lifecycle.lastSaved", { at: formatDateTime(updatedAt, lang), who: updatedBy || "" })}
            </p>
          )}
        </div>
      </div>

      {formError && (
        <p data-testid={LIFECYCLE.formError} role="alert" className="text-sm text-rose-600 mb-4">{formError}</p>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* ── Trial ── */}
        <section className="rounded-2xl bg-white border border-slate-200 p-6">
          <h3 className="font-sans font-semibold text-lg mb-4 text-slate-800">{t("lifecycle.trial.title")}</h3>

          <label htmlFor="lifecycle-trial-length-input" className="text-sm font-medium text-slate-700">
            {t("lifecycle.trial.lengthLabel")}
          </label>
          <div>
            <input
              id="lifecycle-trial-length-input"
              type="number"
              min={trialLengthBounds.min}
              max={trialLengthBounds.max}
              value={draft.trial.length_days}
              onChange={(e) => setField("trial.length_days", Number(e.target.value))}
              disabled={readOnly}
              data-testid={LIFECYCLE.trialLength}
              aria-describedby={violations["trial.length_days"] ? LIFECYCLE.fieldError("trial.length_days") : undefined}
              className="w-24 border border-slate-200 rounded-lg px-3 py-2 text-sm mt-1"
            />
          </div>
          <p className="text-xs text-slate-400 mt-1">
            {t("lifecycle.trial.lengthHint", { min: trialLengthBounds.min, max: trialLengthBounds.max })}
          </p>
          <p className="text-xs text-slate-400">{t("lifecycle.trial.newSignupsOnly")}</p>
          <FieldViolations path="trial.length_days" violations={violations} t={t} />

          <div className="mt-4">
            <LeadDaysEditor
              id="lifecycle-lead-days"
              labelKey="lifecycle.trial.remindersTitle"
              values={draft.trial.reminders.lead_days}
              onChange={(next) => setField("trial.reminders.lead_days", next)}
              disabled={readOnly}
              max={maxReminders}
              dayMin={leadDayBounds.min}
              dayMax={Math.min(leadDayBounds.max, trialLength - 1)}
              order="desc"
              unitKey="lifecycle.leadDay.unit"
              testIds={{
                editor: LIFECYCLE.leadDaysEditor,
                chip: LIFECYCLE.leadDay,
                chipRemove: LIFECYCLE.leadDayRemove,
                input: LIFECYCLE.leadDayInput,
                add: LIFECYCLE.leadDayAdd,
              }}
            />
            <p className="text-xs text-slate-400 mt-1">
              {t("lifecycle.trial.remindersHint", { max: maxReminders })}
            </p>
            <FieldViolations path="trial.reminders.lead_days" violations={violations} t={t} />
          </div>

          <EmailToggle moment="trial_reminder" draft={draft} setField={setField} violations={violations} t={t} disabled={readOnly} />
          <EmailToggle moment="trial_converted" draft={draft} setField={setField} violations={violations} t={t} disabled={readOnly} />
        </section>

        {/* ── Welcome & plan changes ── */}
        <section className="rounded-2xl bg-white border border-slate-200 p-6">
          <h3 className="font-sans font-semibold text-lg mb-4 text-slate-800">{t("lifecycle.other.title")}</h3>
          <EmailToggle moment="welcome" draft={draft} setField={setField} violations={violations} t={t} disabled={readOnly} />
          <EmailToggle moment="plan_changed" draft={draft} setField={setField} violations={violations} t={t} disabled={readOnly} />

          <h4 className="font-sans font-semibold text-sm mt-6 mb-2 text-slate-700">{t("lifecycle.cancellation.title")}</h4>
          <EmailToggle moment="subscription_cancelled" draft={draft} setField={setField} violations={violations} t={t} disabled={readOnly} />
          <EmailToggle moment="service_ended" draft={draft} setField={setField} violations={violations} t={t} disabled={readOnly} />
        </section>

        {/* ── Dunning ── */}
        <section className="rounded-2xl bg-white border border-slate-200 p-6 lg:col-span-2">
          <h3 className="font-sans font-semibold text-lg mb-4 text-slate-800">{t("lifecycle.dunning.title")}</h3>

          <label htmlFor="lifecycle-grace-days-input" className="text-sm font-medium text-slate-700">
            {t("lifecycle.dunning.graceLabel")}
          </label>
          <div>
            <input
              id="lifecycle-grace-days-input"
              type="number"
              min={graceBounds.min}
              max={graceBounds.max}
              value={draft.dunning.grace_days}
              onChange={(e) => setField("dunning.grace_days", Number(e.target.value))}
              disabled={readOnly}
              data-testid={LIFECYCLE.graceDays}
              aria-describedby={violations["dunning.grace_days"] ? LIFECYCLE.fieldError("dunning.grace_days") : undefined}
              className="w-24 border border-slate-200 rounded-lg px-3 py-2 text-sm mt-1"
            />
          </div>
          <p className="text-xs text-slate-400 mt-1">
            {t("lifecycle.dunning.graceHint", { min: graceBounds.min, max: graceBounds.max })}
          </p>
          <FieldViolations path="dunning.grace_days" violations={violations} t={t} />

          <EmailToggle moment="dunning_first_notice" draft={draft} setField={setField} violations={violations} t={t} disabled={readOnly} />

          <EmailToggle moment="dunning_follow_up" draft={draft} setField={setField} violations={violations} t={t} disabled={readOnly} />

          <div className="mt-4">
            <LeadDaysEditor
              id="lifecycle-follow-ups"
              labelKey="lifecycle.dunning.followUpsTitle"
              values={draft.dunning.follow_ups.days_after_failure}
              onChange={(next) => setField("dunning.follow_ups.days_after_failure", next)}
              disabled={readOnly}
              max={maxFollowUps}
              dayMin={followUpMinDay}
              dayMax={graceDays - 1}
              order="asc"
              unitKey="lifecycle.followUpDay.unit"
              testIds={{
                editor: LIFECYCLE.followUpEditor,
                chip: LIFECYCLE.followUp,
                chipRemove: LIFECYCLE.followUpRemove,
                input: LIFECYCLE.followUpInput,
                add: LIFECYCLE.followUpAdd,
              }}
            />
            <p className="text-xs text-slate-400 mt-1">{t("lifecycle.dunning.followUpsHint")}</p>
            <FieldViolations path="dunning.follow_ups.days_after_failure" violations={violations} t={t} />
          </div>

          <EmailToggle moment="suspended_non_payment" draft={draft} setField={setField} violations={violations} t={t} disabled={readOnly} />
        </section>
      </div>

      {stripeControlled.length > 0 && (
        <div data-testid={LIFECYCLE.stripeNote} className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-xs text-slate-500 mt-6">
          <p className="font-medium text-slate-600 mb-1">{t("lifecycle.stripeNote.title")}</p>
          <ul className="list-disc list-inside">
            {stripeControlled.map((key) => <li key={key}>{t(`lifecycle.stripe.${key}`)}</li>)}
          </ul>
        </div>
      )}

      {canSave && (
      <div className="flex items-center gap-3 mt-6">
        <Button
          type="button"
          onClick={() => setConfirming(true)}
          disabled={!dirty || saving}
          data-testid={LIFECYCLE.save}
          className="bg-sol-gold text-sol-midnight hover:bg-sol-gold/90"
        >
          {t("lifecycle.save")}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => { reset(); setFormError(""); }}
          disabled={!dirty}
          data-testid={LIFECYCLE.reset}
        >
          {t("lifecycle.reset")}
        </Button>
      </div>
      )}

      <div className="mt-8 rounded-2xl bg-white border border-slate-200 p-6">
        <UpcomingSends api={api} canRun={canRun} onRan={() => setSendsRefresh((n) => n + 1)} />
      </div>
      <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-6">
        <RecentSends api={api} refreshKey={sendsRefresh} />
      </div>

      {canSave && (
      <StepUpConfirmDialog
        open={confirming}
        onClose={(didMutate) => { setConfirming(false); if (didMutate) reload(); }}
        titleKey="lifecycle.confirmTitle"
        stepUpPromptKey="gw.stepup.promptPolicy"
        descKey="lifecycle.confirmDesc"
        requireAck
        ackKey="lifecycle.ack"
        onConfirm={confirmSave}
      />
      )}
    </div>
  );
}
